import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { Eraser, Globe } from 'lucide-react';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { patchMyTutorProfile } from '@/lib/tutor-api';
import {
  DAYS_OF_WEEK,
  type DayOfWeek,
  type AvailabilityRule,
  type TutorProfileResponse,
} from '@/lib/schemas/tutor-profile';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { selectClass } from './shared';

const pad2 = (n: number) => String(n).padStart(2, '0');

const DAY_LABELS: Record<DayOfWeek, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

const COMMON_TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Dhaka',
  'Asia/Kathmandu',
  'Asia/Colombo',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Africa/Johannesburg',
  'Africa/Cairo',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
  'Pacific/Auckland',
  'UTC',
];

function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
  } catch {
    return 'Asia/Kolkata';
  }
}

type AvailabilityGrid = Record<DayOfWeek, boolean[]>;

function emptyGrid(): AvailabilityGrid {
  return DAYS_OF_WEEK.reduce((acc, d) => {
    acc[d] = new Array(24).fill(false);
    return acc;
  }, {} as AvailabilityGrid);
}

function rulesToGrid(rules: ReadonlyArray<AvailabilityRule>): AvailabilityGrid {
  const grid = emptyGrid();
  for (const r of rules) {
    const [sh = 0, sm = 0] = r.startLocal.split(':').map(Number);
    const startHour = Math.floor(sh + sm / 60);
    let endHour: number;
    if (r.endLocal === '24:00') {
      endHour = 24;
    } else {
      const [eh = 0, em = 0] = r.endLocal.split(':').map(Number);
      endHour = Math.ceil(eh + em / 60);
    }
    for (let h = startHour; h < endHour && h < 24; h++) grid[r.day][h] = true;
  }
  return grid;
}

function gridToRules(grid: AvailabilityGrid): AvailabilityRule[] {
  const rules: AvailabilityRule[] = [];
  for (const day of DAYS_OF_WEEK) {
    const blocks = grid[day];
    let runStart: number | null = null;
    for (let h = 0; h <= 24; h++) {
      const on = h < 24 && blocks[h] === true;
      if (on && runStart === null) {
        runStart = h;
      } else if (!on && runStart !== null) {
        rules.push({
          day,
          startLocal: `${pad2(runStart)}:00`,
          endLocal: h === 24 ? '24:00' : `${pad2(h)}:00`,
        });
        runStart = null;
      }
    }
  }
  return rules;
}

function countSelectedHours(grid: AvailabilityGrid): number {
  let n = 0;
  for (const day of DAYS_OF_WEEK) for (const on of grid[day]) if (on) n++;
  return n;
}

export function ScheduleCard({
  profile,
  onSaved,
}: {
  profile: TutorProfileResponse;
  onSaved: (p: TutorProfileResponse) => void;
}) {
  const { accessToken } = useAuth();
  const [homeTz, setHomeTz] = useState<string>(() => profile.homeTz || detectTimezone());
  const [grid, setGrid] = useState<AvailabilityGrid>(() => rulesToGrid(profile.availability));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setHomeTz(profile.homeTz || detectTimezone());
    setGrid(rulesToGrid(profile.availability));
  }, [profile.homeTz, profile.availability]);

  const paint = useRef<{ active: boolean; value: boolean }>({ active: false, value: true });

  useEffect(() => {
    const stop = () => {
      paint.current.active = false;
    };
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  const setCell = (day: DayOfWeek, hour: number, value: boolean) => {
    setGrid((prev) => {
      if (prev[day][hour] === value) return prev;
      const nextDay = prev[day].slice();
      nextDay[hour] = value;
      return { ...prev, [day]: nextDay };
    });
  };

  const tzOptions = useMemo(() => {
    const set = new Set<string>(COMMON_TIMEZONES);
    set.add(detectTimezone());
    if (homeTz) set.add(homeTz);
    return Array.from(set);
  }, [homeTz]);

  const selectedHours = countSelectedHours(grid);

  const rulesByDay = useMemo(() => {
    const all = gridToRules(grid);
    return DAYS_OF_WEEK.map((day) => ({ day, rules: all.filter((r) => r.day === day) })).filter((d) => d.rules.length > 0);
  }, [grid]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const availability = gridToRules(grid);
    if (availability.length === 0) {
      setError('Select at least one available time block on the grid.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const next = await patchMyTutorProfile(accessToken!, {
        homeTz: homeTz.trim(),
        availability,
        weeklyAvailabilityHours: Math.min(60, selectedHours),
      });
      onSaved(next);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed — please retry.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Schedule</CardTitle>
        <CardDescription>Your weekly availability — parents see these times in their own timezone.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-6" onSubmit={submit}>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          {success && <Alert variant="success"><AlertDescription>Saved — parents can see your updated schedule.</AlertDescription></Alert>}

          {/* Timezone */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-homeTz">
              <span className="inline-flex items-center gap-1.5">
                <Globe className="h-4 w-4 text-brand-600" />
                Your timezone
              </span>
            </Label>
            <select id="edit-homeTz" value={homeTz} onChange={(e) => setHomeTz(e.target.value)} className={selectClass}>
              {tzOptions.map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>)}
            </select>
          </div>

          {/* Hour grid */}
          <fieldset>
            <div className="mb-2 flex items-center justify-between">
              <legend className="text-sm font-semibold text-foreground">Available hours</legend>
              <button
                type="button"
                onClick={() => setGrid(emptyGrid())}
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground cursor-pointer"
              >
                <Eraser className="h-3.5 w-3.5" /> Clear all
              </button>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
              Click or drag to toggle hours. Each cell is one hour on a 24-hour clock.
            </p>

            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-2 sm:p-3 dark:border-slate-700 dark:bg-slate-800/40">
              <div className="min-w-fit select-none">
                <div className="flex">
                  <div className="w-8 shrink-0 sm:w-10" />
                  {Array.from({ length: 24 }, (_, h) => (
                    <div key={h} className="flex-1 text-center text-[8px] leading-none text-muted-foreground sm:text-[9px]">
                      {pad2(h)}
                    </div>
                  ))}
                </div>
                {DAYS_OF_WEEK.map((day) => (
                  <div key={day} className="mt-1 flex items-center">
                    <div className="w-8 shrink-0 pr-1 text-right text-xs font-medium text-foreground sm:w-10">
                      {DAY_LABELS[day]}
                    </div>
                    <div className="flex flex-1 gap-px">
                      {Array.from({ length: 24 }, (_, h) => {
                        const on = grid[day][h] === true;
                        return (
                          <button
                            key={h}
                            type="button"
                            aria-pressed={on}
                            aria-label={`${DAY_LABELS[day]} ${pad2(h)}:00–${pad2(h + 1)}:00`}
                            onMouseDown={() => { const v = !on; paint.current = { active: true, value: v }; setCell(day, h, v); }}
                            onMouseEnter={() => { if (paint.current.active) setCell(day, h, paint.current.value); }}
                            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setCell(day, h, !on); } }}
                            className={`h-6 flex-1 rounded-[3px] transition-colors ${on ? 'bg-brand-600 hover:bg-brand-700' : 'bg-slate-100 hover:bg-brand-200 dark:bg-slate-700 dark:hover:bg-brand-900/50'}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {selectedHours > 0 ? `${selectedHours} hour${selectedHours === 1 ? '' : 's'} selected per week` : 'No hours selected yet'}
            </p>
          </fieldset>

          {/* Summary */}
          {rulesByDay.length > 0 && (
            <div className="rounded-xl border border-brand-100 bg-brand-50/60 p-4 dark:border-slate-700 dark:bg-slate-800/60">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400">
                Your weekly availability ({homeTz.replace(/_/g, ' ')})
              </p>
              <ul className="space-y-0.5">
                {rulesByDay.map((d) => (
                  <li key={d.day} className="flex gap-2 text-sm">
                    <span className="w-10 shrink-0 font-medium text-foreground">{DAY_LABELS[d.day]}</span>
                    <span className="text-muted-foreground">{d.rules.map((r) => `${r.startLocal}–${r.endLocal}`).join(', ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end border-t border-slate-100 pt-4 dark:border-slate-700">
            <Button type="submit" disabled={busy} className="min-w-[140px]">
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
